import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactComponent as DeleteIcon } from "assets/icons/Bin/BinOutline.svg";
import { ReactComponent as CalendarDarkIcon } from "assets/icons/Calendar/CalendarDark.svg";
import { ReactComponent as CalendarOutlineIcon } from "assets/icons/Calendar/CalendarOutline.svg";
import { ReactComponent as CircleTickIcon } from "assets/icons/Check/CheckOutline.svg";
import { ReactComponent as CopyIcon } from "assets/icons/File_Copy/FileCopyOutline.svg";
import { ReactComponent as InfoDarkIcon } from "assets/icons/Info/InfoDark.svg";
import { ReactComponent as InfoOutlineIcon } from "assets/icons/Info/InfoOutline.svg";
import { ReactComponent as SettingsDarkIcon } from "assets/icons/Settings/SettingsDark.svg";
import { ReactComponent as SettingsOutlineIcon } from "assets/icons/Settings/SettingsOutline.svg";
import { ReactComponent as TimeDarkIcon } from "assets/icons/Time/TimeDark.svg";
import { ReactComponent as TimeOutlineIcon } from "assets/icons/Time/TimeOutline.svg";
import { ReactComponent as BlueExclamationCircleIcon } from "assets/icons/blueExclamationCircle.svg";
import { ReactComponent as CalendarWithTickIcon } from "assets/icons/calendarWithTick.svg";
import { ReactComponent as DownloadIcon } from "assets/icons/download.svg";
import { ReactComponent as FilterIcon } from "assets/icons/filter.svg";
import { ReactComponent as InfoAltIcon } from "assets/icons/info-alt.svg";
import { ReactComponent as LightBulbIcon } from "assets/icons/lightBulb.svg";
import { ReactComponent as SpaceWeatherEventIcon } from "assets/icons/spaceWeatherEvent.svg";
import { ReactComponent as StopWatchIcon } from "assets/icons/stopWatch.svg";
import IconWrapper, { IconSize } from "components/components/Icon/IconWrapper";
import React from "react";
import styled from "styled-components";

export enum SvgIconName {
  Copy,
  BlueExclamationCircle,
  StopWatch,
  LightBulb,
  CalendarWithTick,
  SpaceWeatherEvent,
  Filter,
  Download,
  InfoAlt,
  CircleTick,
  Delete,
  TimeOutline,
  CalendarOutline,
  SettingsOutline,
  InfoOutline,
  TimeDark,
  CalendarDark,
  SettingsDark,
  InfoDark,
}

export enum ElexonIconName {
  Download = "$",
}

export const ElexonIcon = styled.span<{ $colour?: string }>`
  font-family: elexon-icons;
  color: ${({ $colour }): string => $colour || "inherit"};
`;

const DropdownChevronSpan = styled.span<{ $colour?: string }>`
  width: 10px;
  height: 10px;
  display: inline-block;
  border-right: 1px solid ${({ $colour }): string => $colour || "currentColor"};
  border-bottom: 1px solid ${({ $colour }): string => $colour || "currentColor"};
  transform: translateY(-1px) rotate(45deg);
  transform-origin: center;
`;

export const DropdownChevron: React.FC<{
  colour?: string;
  size?: IconSize;
  className?: string;
}> = ({ colour, size, className }) => (
  <IconWrapper size={size}>
    <DropdownChevronSpan className={className} $colour={colour} />
  </IconWrapper>
);

type SvgIconProps = {
  iconName: SvgIconName;
  colour?: string;
};

const SvgIcon: React.FC<SvgIconProps> = ({ iconName, colour }) => {
  switch (iconName) {
    case SvgIconName.Copy:
      return <CopyIcon fill={colour} />;
    case SvgIconName.BlueExclamationCircle:
      return <BlueExclamationCircleIcon />;
    case SvgIconName.StopWatch:
      return <StopWatchIcon />;
    case SvgIconName.LightBulb:
      return <LightBulbIcon />;
    case SvgIconName.CalendarWithTick:
      return <CalendarWithTickIcon />;
    case SvgIconName.SpaceWeatherEvent:
      return <SpaceWeatherEventIcon />;
    case SvgIconName.Filter:
      return <FilterIcon />;
    case SvgIconName.Download:
      return <DownloadIcon />;
    case SvgIconName.InfoAlt:
      return <InfoAltIcon />;
    case SvgIconName.CircleTick:
      return <CircleTickIcon fill={colour} />;
    case SvgIconName.Delete:
      return <DeleteIcon fill={colour} />;
    case SvgIconName.TimeOutline:
      return <TimeOutlineIcon fill={colour} />;
    case SvgIconName.CalendarOutline:
      return <CalendarOutlineIcon fill={colour} />;
    case SvgIconName.SettingsOutline:
      return <SettingsOutlineIcon fill={colour} />;
    case SvgIconName.InfoOutline:
      return <InfoOutlineIcon fill={colour} />;
    case SvgIconName.TimeDark:
      return <TimeDarkIcon fill={colour} />;
    case SvgIconName.CalendarDark:
      return <CalendarDarkIcon fill={colour} />;
    case SvgIconName.SettingsDark:
      return <SettingsDarkIcon fill={colour} />;
    case SvgIconName.InfoDark:
      return <InfoDarkIcon fill={colour} />;
    default:
      throw new Error(`Unexpected icon name ${iconName}`);
  }
};

type IconProps = {
  iconName: IconProp | SvgIconName | ElexonIconName;
  colour?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  size?: IconSize;
  onClick?: () => void;
};

const isSvgIconName = (
  iconName: IconProp | SvgIconName | ElexonIconName
): iconName is SvgIconName => typeof iconName === "number";

const isElexonIconName = (
  iconName: IconProp | SvgIconName | ElexonIconName
): iconName is ElexonIconName =>
  typeof iconName === "string" &&
  Object.values(ElexonIconName).includes(iconName as ElexonIconName);

const Icon: React.FC<IconProps> = ({
  iconName,
  colour,
  ariaLabel,
  ariaDescribedBy,
  size,
  onClick,
}) => (
  <IconWrapper
    ariaLabel={ariaLabel}
    ariaDescribedBy={ariaDescribedBy}
    size={size}
    onClick={onClick}
  >
    {isElexonIconName(iconName) ? (
      <ElexonIcon $colour={colour}>{iconName}</ElexonIcon>
    ) : isSvgIconName(iconName) ? (
      <SvgIcon iconName={iconName} colour={colour} />
    ) : (
      <FontAwesomeIcon icon={iconName} color={colour} />
    )}
  </IconWrapper>
);

export default Icon;
